// CPC 生态应用中心 — 全球购、CPC 钱包等入口
import { useCurrentAccount, useSuiClientQuery } from '@mysten/dapp-kit';
import clsx from 'clsx';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { PageLayout } from '~/components/Layout/PageLayout';
import { CpcDownloadQr } from '~/components/cpc/CpcDownloadQr';
import { CpcPageHero, CpcSection, CpcSectionTitle, cpcPageContentClass } from '~/components/cpc/CpcPageHero';
import {
	RWAX_APP_MIRROR_URL,
	RWAX_APP_URL,
	RWAX_IOS_APP_URL,
	WALLET_APK_MIRROR_URL,
	WALLET_APK_URL,
	WALLET_EXTENSION_URL,
} from '~/config/cpcWalletLinks';
import { useT } from '~/i18n/LocaleContext';
import { CPC_SYMBOL, DGC_TYPE_ARG } from '~/utils/cpcCoin';
import { formatMistAsDgc } from '~/utils/explorerFormat';

const GLOBAL_SHOP_URL = 'https://global.shoubo.store/';
const GLOBAL_SHOP_APP_URL = 'https://app.shoubo.net/app.php/Nw==';

type AppLink = { label: string; href: string; external?: boolean; download?: boolean };

type AppCardProps = {
	title: string;
	description: string;
	tags: string[];
	icon: ReactNode;
	iconTone?: 'brand' | 'warm';
	primary: AppLink;
	secondary?: AppLink;
	tertiary?: AppLink;
	secondaryTone?: 'neutral' | 'warm';
	/** 提供则在卡片右侧显示扫码下载二维码 */
	qr?: { url: string; label?: string };
	/** 额外二维码（如 iOS），与主 QR 并排 */
	qrSecondary?: { url: string; label?: string };
	/** 卡片底部提示（安装信任说明等） */
	note?: string;
	noteTitle?: string;
	/** 外层额外 class（如占满整行） */
	className?: string;
};

const iconToneStyles = {
	brand: {
		box: 'bg-[#38BDF8]/15 text-[#38BDF8]',
		tag: 'bg-[#38BDF8]/10 text-[#38BDF8]',
		btn: 'border-[#38BDF8]/50 bg-[#38bff81a] text-[#38BDF8] hover:bg-[#38BDF8] hover:text-white',
	},
	warm: {
		box: 'bg-[#fb923c]/15 text-[#fb923c]',
		tag: 'bg-[#fb923c]/10 text-[#fb923c]',
		btn: 'border-[#fb923c]/50 bg-[#fb923c]/10 text-[#fb923c] hover:bg-[#fb923c] hover:text-white',
	},
};

function AppCard({
	title,
	description,
	tags,
	icon,
	iconTone = 'brand',
	primary,
	secondary,
	tertiary,
	secondaryTone = 'neutral',
	qr,
	qrSecondary,
	note,
	noteTitle,
	className,
}: AppCardProps) {
	const t = useT();
	const tone = iconToneStyles[iconTone];
	const secondaryBtnClass =
		secondaryTone === 'warm'
			? 'border-[#fb923c]/40 text-[#fb923c] hover:border-[#fb923c] hover:bg-[#fb923c]/10'
			: 'border-white/15 text-[#cbd5e1] hover:border-[#38BDF8]/40 hover:bg-[#38BDF8]/10 hover:text-[#38BDF8]';
	const linkProps = (link: AppLink) =>
		link.external ? ({ target: '_blank' as const, rel: 'noopener noreferrer' as const }) : {};
	const hasQr = Boolean(qr || qrSecondary);
	/** 双端下载（如 RWAX）：上方介绍，下方安卓 / 苹果各占一半 */
	const stackedDownloads = Boolean(qr && qrSecondary);

	const fullBtn = (link: AppLink, className: string) => (
		<a
			href={link.href}
			{...linkProps(link)}
			{...(link.download ? { download: true } : {})}
			className={clsx(
				'inline-flex w-full items-center justify-center rounded-xl border px-3 py-2 text-sm font-semibold no-underline transition-all duration-200',
				className,
			)}
		>
			{link.label}
		</a>
	);

	return (
		<div
			className={clsx(
				'group relative flex h-full flex-col overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] p-5 transition-all duration-300',
				'hover:border-[#38BDF8]/40 hover:shadow-[0_0_24px_rgba(56,189,248,0.12)]',
				className,
			)}
		>
			<div
				className="pointer-events-none absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-[#38BDF8]/10 via-[#38BDF8]/5 to-transparent opacity-80"
			/>
			<div
				className={clsx(
					'relative flex flex-1 gap-4',
					hasQr && !stackedDownloads ? 'flex-col sm:flex-row sm:items-stretch' : 'flex-col',
				)}
			>
				<div className="flex min-w-0 flex-1 flex-col">
					<div className="flex items-start justify-between gap-3">
						<div className={clsx('flex h-12 w-12 shrink-0 items-center justify-center rounded-xl', tone.box)}>
							{icon}
						</div>
						<div className="flex flex-wrap justify-end gap-1.5">
							{tags.map((tag) => (
								<span
									key={tag}
									className={clsx(
										'rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
										tone.tag,
									)}
								>
									{tag}
								</span>
							))}
						</div>
					</div>
					<h3 className="relative mt-4 text-lg font-bold text-[#f8fafc]">{title}</h3>
					<p className="relative mt-2 flex-1 text-sm leading-relaxed text-[#aeb9d0]">{description}</p>

					{stackedDownloads ? (
						<div className="relative mt-5 grid gap-3 sm:grid-cols-2">
							{/* Android：左半 */}
							<div className="rounded-xl border border-white/10 bg-white/[0.02] p-3.5">
								<p className="text-[11px] font-semibold uppercase tracking-wide text-[#94a3b8]">
									Android
								</p>
								<div className="mt-3 flex flex-col items-center gap-3">
									{qr ? (
										<CpcDownloadQr
											url={qr.url}
											label={qr.label ?? t('apps.scanDownload')}
											size={104}
										/>
									) : null}
									<div className="flex w-full flex-col gap-2">
										{fullBtn(primary, tone.btn)}
										{secondary
											? fullBtn(
													secondary,
													clsx('font-medium', secondaryBtnClass),
												)
											: null}
									</div>
								</div>
							</div>
							{/* iOS：右半 */}
							<div className="rounded-xl border border-[#fb923c]/25 bg-[#fb923c]/[0.04] p-3.5">
								<p className="text-[11px] font-semibold uppercase tracking-wide text-[#fb923c]">
									iOS / Apple
								</p>
								<div className="mt-3 flex flex-col items-center gap-3">
									{qrSecondary ? (
										<CpcDownloadQr
											url={qrSecondary.url}
											label={qrSecondary.label ?? t('apps.scanDownload')}
											size={104}
										/>
									) : null}
									<div className="flex w-full flex-col gap-2">
										{tertiary
											? fullBtn(
													tertiary,
													'border-[#fb923c]/50 bg-[#fb923c]/10 text-[#fb923c] hover:bg-[#fb923c] hover:text-white',
												)
											: null}
									</div>
								</div>
							</div>
						</div>
					) : (
						<>
							<div className="relative mt-5 flex flex-wrap gap-2">
								<a
									href={primary.href}
									{...linkProps(primary)}
									{...(primary.download ? { download: true } : {})}
									className={clsx(
										'inline-flex items-center justify-center rounded-xl border px-4 py-2 text-sm font-semibold no-underline transition-all duration-200',
										tone.btn,
									)}
								>
									{primary.label}
								</a>
								{secondary ? (
									<a
										href={secondary.href}
										{...linkProps(secondary)}
										{...(secondary.download ? { download: true } : {})}
										className={clsx(
											'inline-flex items-center justify-center rounded-xl border px-4 py-2 text-sm font-medium no-underline transition-colors',
											secondaryBtnClass,
										)}
									>
										{secondary.label}
									</a>
								) : null}
								{tertiary ? (
									<a
										href={tertiary.href}
										{...linkProps(tertiary)}
										{...(tertiary.download ? { download: true } : {})}
										className="inline-flex items-center justify-center rounded-xl border border-[#fb923c]/40 px-4 py-2 text-sm font-medium text-[#fb923c] no-underline transition-colors hover:border-[#fb923c] hover:bg-[#fb923c]/10"
									>
										{tertiary.label}
									</a>
								) : null}
							</div>
						</>
					)}
				</div>
				{hasQr && !stackedDownloads ? (
					<div className="flex shrink-0 items-center justify-center border-t border-white/10 pt-4 sm:border-l sm:border-t-0 sm:pl-4 sm:pt-0">
						{qr ? (
							<CpcDownloadQr url={qr.url} label={qr.label ?? t('apps.scanDownload')} size={120} />
						) : null}
					</div>
				) : null}
			</div>
			{note ? (
				<div className="relative mt-4 rounded-xl border border-[#fb923c]/25 bg-[#fb923c]/[0.07] px-3.5 py-3">
					{noteTitle ? (
						<p className="text-[11px] font-semibold uppercase tracking-wide text-[#fb923c]">
							{noteTitle}
						</p>
					) : null}
					<p className={clsx('text-xs leading-relaxed text-[#fdba74]', noteTitle && 'mt-1.5')}>
						{note}
					</p>
				</div>
			) : null}
		</div>
	);
}

function AppIcon({ children }: { children: ReactNode }) {
	return <span className="flex h-6 w-6 items-center justify-center [&>svg]:h-6 [&>svg]:w-6">{children}</span>;
}

function GlobeIcon() {
	return (
		<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
			<circle cx="12" cy="12" r="10" />
			<path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
		</svg>
	);
}

function PhoneIcon() {
	return (
		<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
			<rect x="6" y="2" width="12" height="20" rx="2" />
			<line x1="12" y1="18" x2="12" y2="18.01" strokeWidth="2" strokeLinecap="round" />
		</svg>
	);
}

function WalletIcon() {
	return (
		<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
			<path d="M19 7H18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-1" />
			<path d="M3 7h16v10H3z" />
			<circle cx="17" cy="12" r="1" fill="currentColor" stroke="none" />
		</svg>
	);
}

function TradeIcon() {
	return (
		<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
			<path d="M7 17 17 7M7 7h10v10" />
		</svg>
	);
}

function BridgeIcon() {
	return (
		<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
			<path d="M4 14h16M8 10l4-4 4 4M8 18l4 4 4-4" />
		</svg>
	);
}

function StakeIcon() {
	return (
		<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
			<path d="M12 3v18M5 10l7-7 7 7M5 14l7 7 7-7" />
		</svg>
	);
}

function ExplorerIcon() {
	return (
		<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
			<circle cx="11" cy="11" r="7" />
			<path d="m20 20-3.5-3.5" />
		</svg>
	);
}

export default function CpcWalletPage() {
	const t = useT();
	const account = useCurrentAccount();
	const { data: chainId } = useSuiClientQuery('getChainIdentifier', {});
	const { data: bal } = useSuiClientQuery(
		'getBalance',
		{ owner: account?.address ?? '', coinType: DGC_TYPE_ARG },
		{ enabled: !!account?.address },
	);
	const addressBalance =
		(bal as { fundsInAddressBalance?: string } | undefined)?.fundsInAddressBalance ?? bal?.totalBalance;

	const shortcuts = [
		{ to: '/trade', label: t('apps.dexTrade'), desc: t('apps.dexTradeDesc'), icon: <TradeIcon /> },
		{ to: '/bridge', label: t('apps.bridge'), desc: t('apps.bridgeDesc'), icon: <BridgeIcon /> },
		{ to: '/stake', label: t('apps.stake'), desc: t('apps.stakeDesc'), icon: <StakeIcon /> },
		{ to: '/', label: t('apps.explorer'), desc: t('apps.explorerDesc'), icon: <ExplorerIcon /> },
	];

	return (
		<PageLayout
			gradient={{
				size: 'md',
				content: (
					<CpcPageHero
						title={
							<>
								<span className="text-white">{t('apps.title')}</span>{' '}
								<span className="font-normal text-[#38BDF8]">{t('apps.titleSuffix')}</span>
							</>
						}
						subtitle={t('apps.subtitle')}
						mobileSubtitle={t('apps.mobileSubtitle')}
						actions={
							account ? (
								<Link
									to={`/address/${account.address}`}
									className="inline-flex items-center gap-2 rounded-xl border border-[#38BDF8]/50 bg-[#38bff81a] px-3 py-2 text-sm font-medium text-[#38BDF8] transition-all duration-200 hover:bg-[#38BDF8] hover:text-white"
								>
									{t('apps.viewAccount')}
								</Link>
							) : null
						}
					/>
				),
			}}
			content={
				<div className={`${cpcPageContentClass} pb-6 text-[#e2e8f0] md:pb-10`}>
					<CpcSection>
						<CpcSectionTitle>{t('apps.featured')}</CpcSectionTitle>
						<div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
							<AppCard
								title={t('apps.globalShop')}
								description={t('apps.globalShopDesc')}
								tags={['Web', t('apps.tagEcommerce')]}
								icon={<AppIcon><GlobeIcon /></AppIcon>}
								primary={{ label: t('apps.openGlobalShop'), href: GLOBAL_SHOP_URL, external: true }}
							/>
							<AppCard
								title={t('apps.globalShopApp')}
								description={t('apps.globalShopAppDesc')}
								tags={['Android', t('apps.tagMobile')]}
								icon={<AppIcon><PhoneIcon /></AppIcon>}
								primary={{
									label: t('apps.downloadGlobalShopApp'),
									href: GLOBAL_SHOP_APP_URL,
									external: true,
									download: true,
								}}
								secondary={{ label: t('apps.mirrorDownload'), href: GLOBAL_SHOP_APP_URL, external: true }}
								qr={{ url: GLOBAL_SHOP_APP_URL, label: t('apps.scanDownload') }}
							/>
							<AppCard
								title="DGC Wallet"
								description={t('apps.walletDesc')}
								tags={[t('apps.tagWallet'), 'Web3', 'DGC']}
								icon={<AppIcon><WalletIcon /></AppIcon>}
								primary={{
									label: t('wallet.downloadApp'),
									href: WALLET_APK_URL,
									external: true,
									download: true,
								}}
								secondary={{
									label: t('apps.mirrorDownload'),
									href: WALLET_APK_MIRROR_URL,
									external: true,
								}}
								tertiary={{
									label: t('apps.chromeExt'),
									href: WALLET_EXTENSION_URL,
									external: true,
									download: true,
								}}
								qr={{ url: WALLET_APK_MIRROR_URL, label: t('apps.scanDownloadApk') }}
							/>
							<AppCard
								className="sm:col-span-2"
								title="RWAX"
								description={t('apps.rwaxDesc')}
								tags={[t('apps.tagSuperApp'), 'Android', 'iOS', 'DGC']}
								iconTone="warm"
								icon={<AppIcon><PhoneIcon /></AppIcon>}
								primary={{
									label: t('apps.downloadRwax'),
									href: RWAX_APP_URL,
									external: true,
									download: true,
								}}
								secondary={{
									label: t('apps.mirrorDownload'),
									href: RWAX_APP_MIRROR_URL,
									external: true,
								}}
								tertiary={{
									label: t('apps.downloadRwaxIos'),
									href: RWAX_IOS_APP_URL,
									external: true,
								}}
								secondaryTone="warm"
								qr={{ url: RWAX_APP_URL, label: t('apps.scanDownloadRwax') }}
								qrSecondary={{
									url: RWAX_IOS_APP_URL,
									label: t('apps.scanDownloadRwaxIos'),
								}}
								noteTitle={t('apps.rwaxIosTrustTitle')}
								note={t('apps.rwaxIosTrustTip')}
							/>
						</div>
					</CpcSection>

					<div className="grid gap-4 md:grid-cols-2">
						<CpcSection>
							<CpcSectionTitle>{t('apps.onchainShortcuts')}</CpcSectionTitle>
							<div className="grid gap-2 sm:grid-cols-2">
								{shortcuts.map((item) => (
									<Link
										key={item.to}
										to={item.to}
										className="group flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 transition-all hover:border-[#38BDF8]/40 hover:bg-[#38BDF8]/5"
									>
										<span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#38BDF8]/10 text-[#38BDF8] transition-colors group-hover:bg-[#38BDF8]/20 [&>svg]:h-4 [&>svg]:w-4">
											{item.icon}
										</span>
										<span className="min-w-0">
											<p className="font-semibold text-[#f8fafc] group-hover:text-[#38BDF8]">{item.label}</p>
											<p className="mt-0.5 text-xs text-[#aeb9d0]">{item.desc}</p>
										</span>
									</Link>
								))}
							</div>
						</CpcSection>

						<CpcSection>
							<CpcSectionTitle>{t('apps.myStatus')}</CpcSectionTitle>
							<div className="space-y-4">
								<div>
									<p className="text-xs font-bold uppercase tracking-wider text-[#aeb9d0]">Chain ID</p>
									<p className="mt-1 font-mono text-lg font-bold text-[#38BDF8]">{chainId ?? '—'}</p>
								</div>
								<div>
									<p className="text-xs font-bold uppercase tracking-wider text-[#aeb9d0]">
										{t('trade.balanceLabel').replace('{symbol}', CPC_SYMBOL)}
									</p>
									<div className="mt-1 flex items-baseline gap-2">
										<span className="text-3xl font-black tabular-nums text-[#38BDF8]">
											{account && addressBalance ? formatMistAsDgc(addressBalance) : '—'}
										</span>
										<span className="text-sm font-semibold text-[#aeb9d0]">{CPC_SYMBOL}</span>
									</div>
									{account && bal ? (
										<p className="mt-2 text-xs text-[#94a3b8]">
											{t('apps.totalBalance')} {formatMistAsDgc(bal.totalBalance)}
											{account ? (
												<>
													{' · '}
													<Link
														to={`/address/${account.address}`}
														className="text-[#38BDF8] hover:underline"
													>
														{t('apps.viewAddress')}
													</Link>
												</>
											) : null}
										</p>
									) : (
										<p className="mt-2 text-xs text-[#94a3b8]">{t('apps.connectToSeeBalance')}</p>
									)}
								</div>
							</div>
						</CpcSection>
					</div>
				</div>
			}
		/>
	);
}





